import React from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar, Pie } from "react-chartjs-2";
import { useTable } from "react-table";
import {
  FaBoxOpen,
  FaTags,
  FaMoneyBillWave,
  FaUsers,
  FaShoppingCart,
  FaCalendarDay,
} from "react-icons/fa";
import displayINRCurrency from "../helpers/displayCurrency";

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

const AdminDashboard = () => {
  // Static Data
  const overallCounts = {
    categories: 12,
    brands: 28,
    subCategories: 45,
    products: 312,
    customers: 1248,
    orders: 892,
  };

  const todayOrdersCount = 24;
  const todayRevenue = 84250;
  const totalRevenue = 12456780;

  // Static Today's Orders
  const orderData = [
    {
      order_code: "ORD-2026-001",
      customer_name: "Priya Sharma",
      mobile_number: "9876543210",
      order_amount: 2499,
      delivery_charges: 0,
      total_amount: 2499,
      order_date: "2026-01-06T10:30:00",
      order_status: "Delivered",
      order_address:
        "12/45, Rose Apartments, MG Road, Bangalore, Karnataka - 560001",
      order_id: 1,
    },
    {
      order_code: "ORD-2026-002",
      customer_name: "Ananya Verma",
      mobile_number: "8765432109",
      order_amount: 3599,
      delivery_charges: 99,
      total_amount: 3698,
      order_date: "2026-01-06T11:15:00",
      order_status: "Order in transit",
      order_address: "Flat 301, Lotus Towers, Andheri West, Mumbai - 400058",
      order_id: 2,
    },
    {
      order_code: "ORD-2026-003",
      customer_name: "Riya Kapoor",
      mobile_number: "7654321098",
      order_amount: 1899,
      delivery_charges: 0,
      total_amount: 1899,
      order_date: "2026-01-06T12:00:00",
      order_status: "Processing",
      order_address: "H.No. 56, Green Valley, Sector 42, Gurugram, Haryana",
      order_id: 3,
    },
    {
      order_code: "ORD-2026-004",
      customer_name: "Saanvi Reddy",
      mobile_number: "6543210987",
      order_amount: 4299,
      delivery_charges: 149,
      total_amount: 4448,
      order_date: "2026-01-06T14:20:00",
      order_status: "Delivered",
      order_address: "Villa No.8, Palm Meadows, Hyderabad, Telangana - 500019",
      order_id: 4,
    },
    {
      order_code: "ORD-2026-005",
      customer_name: "Neha Singh",
      mobile_number: "5432109876",
      order_amount: 999,
      delivery_charges: 0,
      total_amount: 999,
      order_date: "2026-01-06T16:45:00",
      order_status: "Cancelled",
      order_address: "A-12, Sunshine Society, Pune, Maharashtra",
      order_id: 5,
    },
  ];

  // Static Product Order Counts for Bar Chart
  const productOrderCounts = [
    { product_name: "Rose Glow Serum", order_count: 142 },
    { product_name: "Hydrating Face Mist", order_count: 118 },
    { product_name: "Vitamin C Cream", order_count: 95 },
    { product_name: "Lip Tint - Coral Kiss", order_count: 87 },
    { product_name: "Silk Hair Oil", order_count: 72 },
    { product_name: "Matte Lipstick Set", order_count: 65 },
  ];

  // Static Best Selling Products
  const bestSellingProducts = [
    { product_name: "Rose Glow Serum", order_count: 142 },
    { product_name: "Hydrating Face Mist", order_count: 118 },
    { product_name: "Vitamin C Cream", order_count: 95 },
    { product_name: "Lip Tint - Coral Kiss", order_count: 87 },
    { product_name: "Silk Hair Oil", order_count: 72 },
  ];

  // Bar Chart Data
  const barData = {
    labels: productOrderCounts.map((p) => p.product_name),
    datasets: [
      {
        label: "Number of Orders",
        data: productOrderCounts.map((p) => p.order_count),
        backgroundColor: "rgba(255, 182, 193, 0.4)",
        borderColor: "rgba(255, 105, 180, 1)",
        borderWidth: 2,
      },
    ],
  };

  const barOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: "top", labels: { color: "#666" } },
      tooltip: {
        callbacks: {
          label: (context) => `${context.label}: ${context.raw} orders`,
        },
      },
    },
    scales: {
      y: { ticks: { color: "#888" } },
      x: { ticks: { color: "#888" } },
    },
  };

  // Pie Chart Data
  const pieData = {
    labels: [
      "Delivered",
      "Processing",
      "Order in transit",
      "Cancelled",
      "Order placed",
    ],
    datasets: [
      {
        data: [18, 3, 2, 2, 1],
        backgroundColor: [
          "#FFB6C1",
          "#FFC0CB",
          "#FFD1DC",
          "#FF69B4",
          "#FF1493",
        ],
        hoverBackgroundColor: [
          "#FF69B4",
          "#FF1493",
          "#DB7093",
          "#C71585",
          "#FF69B4",
        ],
      },
    ],
  };

  const pieOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: "top", labels: { color: "#666" } },
      tooltip: {
        callbacks: {
          label: (context) => `${context.label}: ${context.raw} orders`,
        },
      },
    },
  };

  // Table Columns
  const columns = React.useMemo(
    () => [
      { Header: "Order Code", accessor: "order_code" },
      { Header: "Customer Name", accessor: "customer_name" },
      { Header: "Mobile", accessor: "mobile_number" },
      {
        Header: "Order Amount",
        accessor: "order_amount",
        Cell: ({ value }) => (
          <span className="text-gray-900 font-medium">
            {displayINRCurrency(value)}
          </span>
        ),
      },
      {
        Header: "Delivery Charges",
        accessor: "delivery_charges",
        Cell: ({ value }) => (
          <span className="text-gray-900 font-medium">
            {displayINRCurrency(value)}
          </span>
        ),
      },
      {
        Header: "Total Amount",
        accessor: "total_amount",
        Cell: ({ value }) => (
          <span className="text-gray-900 font-medium">
            {displayINRCurrency(value)}
          </span>
        ),
      },
      {
        Header: "Order Date",
        accessor: "order_date",
        Cell: ({ value }) => (
          <span className="text-gray-900 font-medium">
            {new Date(value).toLocaleDateString("en-IN")}
          </span>
        ),
      },
      {
        Header: "Order Status",
        accessor: "order_status",
        Cell: ({ value }) => {
          let statusClass =
            "px-3 py-1 rounded-full text-xs font-semibold whitespace-nowrap";
          switch (value) {
            case "Pending":
              statusClass += " bg-yellow-100 text-yellow-800";
              break;
            case "Order placed":
              statusClass += " bg-orange-100 text-orange-800";
              break;
            case "Processing":
              statusClass += " bg-purple-100 text-purple-800";
              break;
            case "Order confirmed":
              statusClass += " bg-blue-100 text-blue-800";
              break;
            case "Order in transit":
              statusClass += " bg-teal-100 text-teal-800";
              break;
            case "Delivered":
              statusClass += " bg-pink-100 text-pink-800";
              break;
            case "Cancelled":
              statusClass += " bg-red-100 text-red-800";
              break;
            default:
              statusClass += " bg-gray-100 text-gray-800";
              break;
          }
          return <span className={statusClass}>{value}</span>;
        },
      },
      {
        Header: "Address",
        accessor: "order_address",
        Cell: ({ value }) => {
          const [isExpanded, setIsExpanded] = React.useState(false);
          return (
            <div className="max-w-xs">
              <div
                className={`transition-all duration-300 ${
                  isExpanded ? "whitespace-normal" : "truncate"
                } max-w-[200px]`}
              >
                {value}
              </div>
              <button
                onClick={() => setIsExpanded(!isExpanded)}
                className="text-pink-600 hover:text-pink-800 text-sm underline mt-1"
              >
                {isExpanded ? "Less" : "More"}
              </button>
            </div>
          );
        },
      },
      {
        Header: "Action",
        Cell: ({ row }) => (
          <button className="bg-pink-600 hover:bg-pink-700 text-white font-medium py-2 px-5 rounded-lg transition">
            View
          </button>
        ),
      },
    ],
    []
  );

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
    useTable({ columns, data: orderData });

  return (
    <>
      <h1 className="text-2xl md:text-4xl font-extrabold mb-10 text-pink-800 text-center tracking-wide px-4">
        Dashboard
      </h1>

      {/* Modern Widget Grid - No outer padding */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6 mb-10 px-4">
        {/* Products */}
        <div className="bg-white rounded-2xl shadow-lg border border-pink-100 p-6 flex flex-col items-center hover:shadow-xl transition">
          <FaBoxOpen className="text-pink-600 text-4xl mb-3" />
          <p className="text-gray-600 text-sm font-medium">Products</p>
          <p className="text-3xl font-bold text-pink-800 mt-2">
            {overallCounts.products}
          </p>
        </div>

        {/* Brands */}
        <div className="bg-white rounded-2xl shadow-lg border border-pink-100 p-6 flex flex-col items-center hover:shadow-xl transition">
          <FaTags className="text-pink-600 text-4xl mb-3" />
          <p className="text-gray-600 text-sm font-medium">Brands</p>
          <p className="text-3xl font-bold text-pink-800 mt-2">
            {overallCounts.brands}
          </p>
        </div>

        {/* Total Revenue */}
        <div className="bg-gradient-to-br from-pink-100 to-pink-200 rounded-2xl shadow-lg border border-pink-300 p-6 flex flex-col items-center hover:shadow-xl transition">
          <FaMoneyBillWave className="text-pink-700 text-4xl mb-3" />
          <p className="text-gray-700 text-sm font-medium">Total Revenue</p>
          <p className="text-2xl font-extrabold text-pink-900 mt-2">
            {displayINRCurrency(totalRevenue)}
          </p>
        </div>

        {/* Customers */}
        <div className="bg-white rounded-2xl shadow-lg border border-pink-100 p-6 flex flex-col items-center hover:shadow-xl transition">
          <FaUsers className="text-pink-600 text-4xl mb-3" />
          <p className="text-gray-600 text-sm font-medium">Customers</p>
          <p className="text-3xl font-bold text-pink-800 mt-2">
            {overallCounts.customers.toLocaleString()}
          </p>
        </div>

        {/* Total Orders */}
        <div className="bg-white rounded-2xl shadow-lg border border-pink-100 p-6 flex flex-col items-center hover:shadow-xl transition">
          <FaShoppingCart className="text-pink-600 text-4xl mb-3" />
          <p className="text-gray-600 text-sm font-medium">Total Orders</p>
          <p className="text-3xl font-bold text-pink-800 mt-2">
            {overallCounts.orders}
          </p>
        </div>

        {/* Today's Orders */}
        <div className="bg-gradient-to-br from-pink-100 to-pink-200 rounded-2xl shadow-lg border border-pink-300 p-6 flex flex-col items-center hover:shadow-xl transition">
          <FaCalendarDay className="text-pink-700 text-4xl mb-3" />
          <p className="text-gray-700 text-sm font-medium">Today's Orders</p>
          <p className="text-3xl font-extrabold text-pink-900 mt-2">
            {todayOrdersCount}
          </p>
          <p className="text-sm text-pink-800 mt-1">
            Revenue: {displayINRCurrency(todayRevenue)}
          </p>
        </div>
      </div>

      {/* Today's Orders Table */}
      <div className="bg-white/90 backdrop-blur rounded-2xl shadow-xl mb-8 border border-pink-100 mx-4">
        <div className="p-6">
          <h2 className="text-xl md:text-2xl font-bold mb-6 text-pink-800">
            Today's Orders
          </h2>

          <div className="overflow-x-auto rounded-lg border border-pink-100">
            <table {...getTableProps()} className="w-full text-sm">
              <thead className="bg-pink-100">
                {headerGroups.map((headerGroup) => (
                  <tr {...headerGroup.getHeaderGroupProps()}>
                    {headerGroup.headers.map((column) => (
                      <th
                        {...column.getHeaderProps()}
                        className="p-4 text-left font-semibold text-pink-900"
                      >
                        {column.render("Header")}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody {...getTableBodyProps()} className="bg-white">
                {rows.map((row) => {
                  prepareRow(row);
                  return (
                    <tr
                      {...row.getRowProps()}
                      className="border-b border-pink-50 hover:bg-pink-50 transition"
                    >
                      {row.cells.map((cell) => (
                        <td
                          {...cell.getCellProps()}
                          className="p-4 text-gray-800"
                        >
                          {cell.render("Cell")}
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8 px-4">
        <div className="bg-white/90 p-6 rounded-2xl shadow-xl border border-pink-100">
          <h2 className="text-xl font-bold mb-6 text-pink-800">
            Top Product Sales
          </h2>
          <div style={{ height: "340px" }}>
            <Bar data={barData} options={barOptions} />
          </div>
        </div>
        <div className="bg-white/90 p-6 rounded-2xl shadow-xl border border-pink-100">
          <h2 className="text-xl font-bold mb-6 text-pink-800">
            Order Status Overview
          </h2>
          <div style={{ height: "340px" }}>
            <Pie data={pieData} options={pieOptions} />
          </div>
        </div>
      </div>

      {/* Top Selling Products */}
      <div className="bg-white/90 p-6 rounded-2xl shadow-xl border border-pink-100 mx-4 mb-8">
        <h2 className="text-xl font-bold mb-6 text-pink-800">
          Top 5 Best Selling Products
        </h2>
        <div className="space-y-4">
          {bestSellingProducts.map((product, index) => (
            <div
              key={index}
              className="flex items-center justify-between p-4 bg-pink-50 rounded-xl border border-pink-200"
            >
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-gradient-to-br from-pink-300 to-pink-500 rounded-full flex items-center justify-center text-white font-bold text-lg">
                  {index + 1}
                </div>
                <div>
                  <h3 className="font-semibold text-pink-900 text-lg">
                    {product.product_name}
                  </h3>
                  <p className="text-pink-700">
                    {product.order_count} units sold
                  </p>
                </div>
              </div>
              <div className="text-2xl font-bold text-pink-600">
                {product.order_count}
              </div>
            </div>
          ))}
        </div>
      </div>
    </>
  );
};

export default AdminDashboard;
